<template>
	<div>
		<!-- inventory report -->
		<v-dialog v-model="reportDialog" max-width="86%">
			<v-card>
				<v-card-title>
					<v-btn @click="reportDialog = false" icon>
						<v-icon>$close</v-icon>
					</v-btn>
					<v-spacer/>
					<span class="me-8">
						{{$T('inventory-report')}}
					</span>
					<v-spacer/>
				</v-card-title>
				<v-card-text>
					<!-- filters -->
					<v-form @submit.prevent="generateReport()">
						<v-row dense align="center" class="mb-0">
							<!-- lot -->
							<v-col lg="2" md="3" sm="4" cols="6">
								<v-text-field
									v-model.number="reportDialogFilters.lot"
									:label="$t('inputs.lot')"
									hide-details
									dense
									outlined
								/>
							</v-col>

							<!-- date -->
							<v-col lg="2" md="3" sm="4" cols="6">
								<bee-date-picker
									v-model="reportDialogFilters.date"
									:input-label="$t('inputs.date')"
									:input-props="{
										dense: true,
										outlined: true,
										readonly: true,
										clearable: true
									}"
									menu-picker
									hide-details
									dense
									outlined
								/>
							</v-col>

							<v-col cols="auto">
								<v-btn type="submit" color="primary">
									{{$T('search')}}
								</v-btn>
							</v-col>
						</v-row>
					</v-form>

					<!-- table -->
					<bee-handy-table
						ref="reportTable"
						:headers="reportDialogHeaders"
						:items="[]"
						:items-per-page="20"
						:loading="loadingReport"
						with-columns-divider
						fixed-header
						:height="$vuetify.breakpoint.height - 260"
						zebra
						dense
						pagination-on-scroll
						pagination-on-scroll-server-items
						:server-items-length="totalReportItemsLength"
						shown-rows-text
						:data-table-props="{ disableSort: true }"
						@pagination-on-scroll:reach-last-row="generateReport"
					>
						<template v-slot:body.append="{ headers }">
							<tr class="totals-row text-center">
								<td
									v-for="(header, index) in headers"
									:key="index"
									:class="header.columnClass"
									class="v-data-table__divider font-weight-bold primary--text"
								>
									{{ !loadingReport ? 
										allTotalReports.perItemTotals?.length && allTotalReports.perItemTotals.find(c => c.itemId === header.value) ?
											$options.filters.numberFormat(fixNumber(+allTotalReports.perItemTotals.find(c => c.itemId === header.value)?.totalWeight))
										: null
										: '-'
									}}
								</td>
							</tr>
						</template>
					</bee-handy-table>

					<div class="d-flex justify-center text-body-1 font-weight-bold primary--text pt-2">
						<v-spacer/>
						<div class="px-2" style="">
							{{ $T('total') }}:
							<span
								class="ps-1"
							>
								{{ !loadingReport ? (allTotalReports ? $options.filters.numberFormat(fixNumber(+allTotalReports.grandTotal)) : 0) + ' qq' : '-' }}
							</span>
						</div>
						<v-spacer/>
						<v-spacer/>
						<v-spacer/>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn color="primary" @click="reportDialog = false">
						{{$T('ok')}}
					</v-btn>
					<v-btn v-if="false" color="primary" @click="reportDialog = false">
						<v-icon class="me-1">mdi-table-arrow-right</v-icon>
						{{$T('export-as-excel')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- receive dialog -->
		<v-dialog v-model="receiveDialog" persistent max-width="950">
			<v-form
				ref="form"
				@submit.prevent="submit"
				:disabled="
					submitLoading ||
					loadingPurchase ||
					(receiveItem.status === receiveStatus.received && !isAdmin) ||
					isPreview
				"
			>
				<v-card :loading="loadingPurchase">
					<v-card-title class="pt-2">
						<v-btn @click="receiveDialog = false" icon>
							<v-icon>$close</v-icon>
						</v-btn>
						<v-spacer/>
						<span class="me-8">
							{{
								receiveItem.status === receiveStatus.received
									? (!isAdmin || isPreview ? $T('preview-receiving') : $T('edit-receiving'))
									: $T('confirm-receiving')
							}}
						</span>
						<v-spacer/>
					</v-card-title>
					<v-card-text class="text-body-1 pb-2">
						<v-row>
							<!-- table -->
							<v-col cols="6" class="pt-0">
								<!-- table -->
								<bee-handy-smart-table
									id="dialogTable"
									ref="entiresTable"
									:headers="dialogHeaders"
									:items="tableItems"
									:items-per-page="-1"
									fixed-header
									:height="tableItems.length > 15 ? $vuetify.breakpoint.height - 200 : null"
									dense
									hot-inputs
									hide-default-footer
									hide-inputs-details
									:item-class="item => item.parentId ? 'grey lighten-4' : null"
									:data-table-props="{
										disableSort: true,
										noDataText: $t('messages.select-Item-to-purchase')
									}"
								>
									<template v-slot:item.itemName="{ item }">
										<span :class="{'ms-6': item.parentId}">
											{{item.itemName}}
										</span>
									</template>

									<template v-slot:item.weight="{ item }">
										{{$options.filters.numberFormat(fixNumber(item.weight))}} 
										<span v-if="item.parentId">
											/ {{$options.filters.numberFormat(fixNumber(getParentWeight(item.parentId)))}}
										</span>
						
									</template>

									<!-- percent -->
									<template
										v-if="!$route.query.receivingId || isAdmin"
										v-slot:item.percent="{ item }"
									>
										<div class="d-flex align-center">
											<v-text-field
												v-model.number="item.percent"
												type="number"
												min="0"
												hide-spin-buttons
												dense
												hide-details
												class="mt-0 mb-0"
												:error="
													item.parentId
														? getTotalChildrenPercent(dialogData.entires, item.parentId) !== 100
														: !item.parentId ? getTotalParentPercent(dialogData.entires) !== 100 : false 
												"
												:rules="rules.requiredAsPercent"
												@input="updateItemsWeights"
											/>
											<span
												v-if="item.hasChildren"
												class="text-caption ms-6"
												:class="{'alert-color--text': getTotalChildrenPercent(dialogData.entires, item.id) !== 100}"
											>
												({{getTotalChildrenPercent(dialogData.entires, item.id)}}%)
											</span>
										</div>
									</template>
								</bee-handy-smart-table>
							</v-col>
							
							<v-col cols="6" >
								<v-row dense align="center" class="rounded mb-2 bgColor">
									<!-- lot -->
									<v-col  cols="4" class="p-1 py-1">
										<v-text-field
											v-model.number="dialogData.lot"
											:label="$T('inputs.lot')"
											type="number"
											min="0"
											hide-spin-buttons
											dense
											outlined
											readonly
											hide-details
											:rules="rules.requiredAsNumber"
										/>
									</v-col>
								
									<!-- date -->
									<v-col  cols="4" class="p-1 py-1">
										<v-text-field
											v-model="dialogData.date"
											:label="$T('inputs.date')"
											dense
											outlined
											hide-details
											readonly
											:rules="rules.required"
										></v-text-field>
									</v-col>
								
									<!-- income -->
									<v-col  cols="4" class="p-1 py-1">
										<v-text-field
											v-model="dialogData.income"
											:label="$T('inputs.entry-no')"
											dense
											outlined
											readonly
											hide-details
										/>
									</v-col>
								
									<!-- ownerId -->
									<v-col  cols="4" class="p-1 py-1">
										<v-autocomplete
											v-model="dialogData.ownerId"
											:items="[
												{ id: null, name: 'Ugarit' },
												...owners
											]"
											item-text="name"
											item-value="id"
											:label="$T('inputs.owner')"
											dense
											outlined
											hide-details
										/>
									</v-col>
								
									<!-- provider -->
									<v-col  cols="4" class="p-1 py-1">
										<v-autocomplete
											v-model="dialogData.providerId"
											:items="receiveItem.status === receiveStatus.received ? accounts : providers"
											item-text="name"
											item-value="id"
											:label="$T('inputs.provider')"
											dense
											outlined
											readonly
											hide-details
											:rules="rules.required"
										/>
									</v-col>
								
									<!-- qq-net-weight -->
									<v-col  cols="4" class="p-1 py-1">
										<v-text-field
											v-model="dialogData.weightFormat"
											v-number-format.number="[dialogData.weightFormat, dialogData.weight]"
											:label="$T('inputs.weight')"
											dense
											outlined
											hide-details
											:rules="rules.required"
											@change="updateItemsWeights"
										/>
									</v-col>
								
									<!-- price -->
									<v-col  cols="4" class="p-1 py-1">
										<v-text-field
											v-model="dialogData.priceFormat"
											:label="$T('inputs.price')"
											dense
											outlined
											hide-details
											:rules="rules.required"
											@change="dialogData.totalPurchase = dialogData.weight * dialogData.price"
											v-number-format.number="[dialogData.priceFormat, dialogData.price]"
										/>
									</v-col>

									<v-col cols="4" class="p-1 py-1">
										<v-text-field 
											v-model.number="dialogData.humidity"
											:label="$T('inputs.humidity') + ' (%)'"
											dense
											outlined
											hide-details
										/>
									</v-col>
									<!-- exclude-from-statistics -->
									<v-col cols="6" class="p-1 py-1 mt-0">
										<v-switch
											v-model="dialogData.excludeFromStatistics"
											:label="$T('inputs.exclude-from-statistics')"
											dense
											outlined
											hide-details
											class="mt-1"
										/>
									</v-col>
									<!-- total-price -->
									<v-col cols="8" class="p-1 py-1">
										<v-text-field		
											:label="$T('inputs.total-price')"
											dense
											outlined
											readonly
											hide-details
											:value="dialogData.totalPurchase ? $options.filters.numberFormat(fixNumber(dialogData.totalPurchase)) : 0"
										/>
									</v-col>
								</v-row>

								<v-row>
									<!-- special-invoice -->
									<v-col sm="4" cols="12" class="pe-1 py-1">
										<v-text-field
											v-model="dialogData.specialInvoice"
											:label="$T('inputs.special-invoice')"
											dense
											outlined
											hide-details
										></v-text-field>
									</v-col>
							
									<!-- credit-note -->
									<v-col sm="4" cols="12" class="pe-1 py-1">
										<v-text-field
											v-model="dialogData.creditNote"
											:label="$T('inputs.credit-note')"
											dense
											outlined
											hide-details
										></v-text-field>
									</v-col>
									
									<!-- paco-reference -->
									<v-col sm="4" cols="12" class="pe-1 py-1">
										<v-text-field
											v-model="dialogData.pacoReference"
											:label="$T('inputs.paco-reference')"
											dense
											outlined
											hide-details
										></v-text-field>
									</v-col>
							
									<!-- notes -->
									<v-col cols="12" class="pe-1 py-1">
										<v-textarea
											v-model="dialogData.notes"
											:label="$T('inputs.notes')"
											dense
											outlined
											hide-details
											rows="1"
											no-resize
											auto-grow
										></v-textarea>
									</v-col>

									<v-divider class="mt-3 mb-2"/>
									<div class="d-flex text-body-1 font-weight-bold primary--text">
										<div class="px-2" style="">
											Total
										</div>
										<v-spacer/>
										<v-spacer/>
										<div
											:class="{'red--text': getTotalParentPercent(dialogData.entires) !== 100}"
											class="ps-2"
											style=""
										>
											({{getTotalParentPercent(dialogData.entires)}}%)
											<v-icon
												v-if="getTotalParentPercent(dialogData.entires) !== 100"
												color="red"
											>
												$alert
											</v-icon>
										</div>
									</div>
								</v-row>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-btn
							v-if="receiveItem.status === receiveStatus.received"
							outlined
							:ripple="false"
							color="primary"
							class="me-1"
							@click="generatePrint"
						><v-icon>$print</v-icon></v-btn>
						<v-spacer/>
						<v-btn
							v-if="receiveItem.status !== receiveStatus.received || (isAdmin && !isPreview)"
							type="submit"
							color="primary"
							:loading="submitLoading"
							:disabled="loadingPurchase"
						>
							{{
								receiveItem.status === receiveStatus.received && isAdmin
									? $T('save')
									: $T('receive')
							}}
						</v-btn>
						<v-btn @click="receiveDialog = false" :disabled="submitLoading">{{$T('close')}}</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<template v-if="0">
			<summary>
				<abbr title="test">test</abbr>
			</summary>
			<s><mark>
				test
			</mark></s>
			
			<ruby>test <rt>test</rt></ruby>
		</template>

		<!-- filters -->
		<v-form @submit.prevent="filter(); fetchReceivesStatics();">
			<v-row dense align="center" class="mb-0">
				<v-col lg="2" md="3" sm="4" cols="6">
					<bee-date-picker
						v-model="filterData.date"
						:input-label="$T('inputs.date')"
						:input-props="{
							dense: true,
							outlined: true,
							readonly: true,
							clearable: true,
							prependInnerIcon: '$calendar',
						}"
						menu-picker
						hide-details
					/>
				</v-col>

				<v-col sm="auto" cols="12">
					<v-radio-group v-model="filterData.status" row dense hide-details class="ma-0">
						<span class="me-2">{{$T('status')}}</span>
						<v-radio :label="$T('all')" :value="null"></v-radio>
						<v-radio :label="$T('pending')" :value="receiveStatus.unReceived"></v-radio>
						<v-radio :label="$T('received')" :value="receiveStatus.received"></v-radio>
					</v-radio-group>
				</v-col>

				<v-col cols="auto">
					<v-btn small type="submit" color="primary">
						<v-icon>$search</v-icon>
					</v-btn>
				</v-col>

				<v-spacer/>

				<v-btn small color="primary" @click="reportDialog = true">
					{{$T('generate-report')}}
				</v-btn>
			</v-row>
		</v-form>

		<!-- table -->
		<bee-handy-table
			ref="table"
			:headers="headers"
			:items="[]"
			:items-per-page="20"
			:loading="loading"
			fixed-header
			zebra
			dense
			:height="$vuetify.breakpoint.height - 165"
			pagination-on-scroll
			pagination-on-scroll-server-items
			:server-items-length="totalItems"
			shown-rows-text
			:data-table-props="{ disableSort: true }"
			:item-class="(item) => item.status === receiveStatus.received ? 'light-green lighten-4' : null"
			@pagination-on-scroll:reach-last-row="filter"
		>
			<template v-slot:item.ownerId="{ item }">
				<!-- <span :class="{'orange--text text--darken-4': item.ownerId !== 'Us'}"> -->
				<span :class="{'orange--text text--darken-4': item.ownerId}">
					{{item.ownerId ? getAccountById(item.ownerId).name : 'Ugarit'}}
				</span>
			</template>

			<!-- actions -->
			<template v-slot:item.actions="{ item }">
				<!-- <template v-if="!item.isReceived"> -->
				<template v-if="item.status !== receiveStatus.received">
					<v-btn
						v-if="permissions.receives.isReadWrite"
						dark
						depressed
						small
						color="primary"
						class="me-1"
						@click="receiveDialog = true; receiveItem = item; isPurchase = true"
					>
						{{ $T('receive') }}
					</v-btn>
				</template>

				<div v-else class="d-flex align-center primary--text">
					<template v-if="isAdmin">
						<v-btn
							depressed
							width="34"
							height="34"
							icon
							small
							color="warning-color"
							@click="receiveDialog = true; receiveItem = item;"
						>
							<v-icon>$edit</v-icon>
						</v-btn>
						<v-btn
							depressed
							width="34"
							height="34"
							icon
							small
							color="preview-color"
							class="me-2"
							@click="receiveDialog = true; receiveItem = item; isPreview = true;"
						>
							<v-icon>$view</v-icon>
						</v-btn>

						<v-tooltip top>
							<template v-slot:activator="{ attr, on }">
								<v-btn
									width="34"
									height="34"
									icon
									class="ms-1"
									color="#013859"
									:to="{
										name: 'compare',
										params: {
											id: item.id
										},
										query: {
											type: 'purchase',
											purchaseId: item.purchaseId
										}
									}"
									v-on="on"
									v-bind="attr"
								>
									<v-icon>$compare</v-icon>
								</v-btn>
							</template>
							{{ $T('compare') }}
						</v-tooltip>
					</template>

					<v-btn
						v-else-if="isAdmin || !permissions.receives.isNone"
						dark
						depressed
						small
						color="primary"
						class="me-2"
						@click="receiveDialog = true; receiveItem = item; isPreview = true"
					>
						<v-icon>$view</v-icon>
					</v-btn>

					{{$T('received')}}
					<v-icon
						color="primary"
						size="20"
						class="ms-1"
					>$check</v-icon>
				</div>
			</template>
		</bee-handy-table>

		<v-footer app inset elevation="10" class="justify-space-around grey lighten-2">
			<div>
				{{$T('average-purchases')}}:
				<span class="ms-2 font-weight-bold">
					{{
						$options.filters.numberFormat(
							fixNumber(receivesStatics.sumPurchases / (receivesStatics.sumWeightsOfPurchases || 1))
						)
					}} Q
				</span>
			</div>
			<div>
				{{$T('sum-purchases')}}:
				<span class="ms-2 font-weight-bold">
					{{$options.filters.numberFormat(fixNumber(receivesStatics.sumPurchases, 3))}} Q
				</span>
			</div>
			<div>
				{{$T('total-purchases-weights')}}:
				<span class="ms-2 font-weight-bold">
					{{$options.filters.numberFormat(fixNumber(receivesStatics.sumWeightsOfPurchases, 3))}} qq
				</span>
			</div>
		</v-footer>
	</div>
</template>

<script>
import moment from 'moment';
import { mapState, mapGetters } from 'vuex';
import rules from '@/helpers/validation-rules';
import { receiveStatus, itemCategory } from '@/helpers/enums';
import { fixNumber } from '@/helpers/functions';
import printJS from 'print-js';
import { generateHtmlPdf } from '@/helpers/generate-html';

export default {
	name: 'Receiving',

	data() {
		return {
			totalPurchasen: null,
			loading: false,
			loadingPurchase: false,
			loadingReport: false,
			submitLoading: false,
			isPreview: false,

			reportDialog: false,
			totalReportItemsLength: 0,
			reportDialogFilters: {
				lot: null,
				date: null
			},

			receiveDialog: false,

			receiveItem: {},

			receiveStatus,
			rules,

			// filter
			filterData: {
				date: null,
				status: null,
			},

			// pagination
			totalItems: 0,

			// data
			dialogData: {
				lot: null,
				date: null,
				income: null,
				ownerId: null,
				providerId: null,
				weight: null,
				price: null,
				totalPurchase: null,

				specialInvoice: null,
				creditNote: null,
				pacoReference: null,
				notes: null,
				isSeed: false,

				entires: [],
				priceFormat: null,
				weightFormat: null,
				humidity: 0,
				excludeFromStatistics: null
			},
			itemCategory,
			isPurchase: false,
			purchases: [],
			allTotalReports: []
		}
	},

	watch: {
		reportDialog(val) {
			if (val) {
				this.generateReport();
			} else {
				this.totalReportItemsLength = 0;
			}
		},

		async receiveDialog(val) {
			if (val) {
				this.loadingPurchase = true;
				if (this.$route.query.receivingId) {
					this.receiveItem = await this.$store.dispatch('receives/fetchById', {
						id: this.$route.query.receivingId
					})
				}

				const isReceived = this.receiveItem.status === receiveStatus.received;
				Promise.all([
					this.$store.dispatch('purchases/fetchById', {
						id: this.receiveItem.purchaseId
					}),
					isReceived
						? this.$store.dispatch('receives/fetchById', { id: this.receiveItem.id })
						: (this.$route.query.receivingId ? this.receiveItem : null)
				]).then(([purchaseData, receiveData]) => {
					this.dialogData.lot = this.receiveItem.lot;
					this.dialogData.date = moment(purchaseData.date).format('YYYY-MM-DD');
					this.dialogData.income = purchaseData.entryNo;
					this.dialogData.ownerId = purchaseData.ownerId;
					this.dialogData.providerId = purchaseData.providerId;
					this.dialogData.price = !receiveData ? purchaseData.administrationPrice : receiveData.price;
					this.dialogData.priceFormat = !receiveData ? this.$options.filters.numberFormat(purchaseData.administrationPrice) : this.$options.filters.numberFormat(receiveData.price)
					this.dialogData.humidity = this.receiveItem.humidity || null;
					this.dialogData.excludeFromStatistics = this.receiveItem.excludeFromStatistics;
					this.dialogData.isSeed = purchaseData.isSeed;

					// receive data
					this.updatePurchaseEntires();
					this.dialogData.weight = this.receiveItem.qqNetWeight;
					this.dialogData.weightFormat = this.$options.filters.numberFormat(this.receiveItem.qqNetWeight);
					this.dialogData.totalPurchase = fixNumber(this.dialogData.weight * this.dialogData.price);
					this.dialogData.specialInvoice = this.receiveItem.specialInvoice;
					this.dialogData.creditNote = this.receiveItem.creditNote;
					this.dialogData.pacoReference = this.receiveItem.pacoReference;
					this.dialogData.notes = this.receiveItem.note;
					if (this.isPurchase) {
						this.dialogData.entires.forEach((entry, index) => {
							const item = purchaseData.items.find(c => c.itemId === entry.id);
							const parent = purchaseData.items.find(c => c.itemId === entry.parentId);
							if (item) {
								this.dialogData.entires[index].percent = item.percent;
								this.dialogData.entires[index].weight =
									fixNumber(
										(item.percent / 100) *
										(!entry.parentId ? this.dialogData.weight : parent.weight)
									);
							}
						})
					} else {
						this.dialogData.entires.forEach((entry, index) => {
							const item = this.receiveItem.items.find(c => c.itemId === entry.id);
							const parent = this.receiveItem.items.find(c => c.itemId === entry.parentId);
							if (item) {
								this.dialogData.entires[index].percent = item.percent;
								this.dialogData.entires[index].weight =
									fixNumber(
										(item.percent / 100) *
										(!entry.parentId ? this.dialogData.weight : parent?.weight)
									);
							}
						})
					}
				}).finally(() => {
					this.loadingPurchase = false;
				});
			} else {
				if (this.$route.query.receivingId) this.$router.go(-1);
				this.$refs.form.reset();
				this.dialogData.entires = [];
				this.receiveItem = {};
				this.isPreview = false;
				this.dialogData.isSeed = false;
				this.dialogData.totalPurchase = null;
				this.isPurchase = false
			}
		},
	},

	computed: {
		...mapState({
			isAdmin: state => state.auth.self.isAdmin,
			permissions: state => state.auth.self.permissions,
			owners: state => state.configs.configsTrees.owners.filter(c => !c.isArchived),
			providers: state => state.configs.configsTrees.providers,
			cardamomRootId: state => state.configs.itemsConfigs.cardamomRootId,
			receivesStatics: state => state.receives.receivesStatics,
			accounts: state => state.accounts.accounts.filter(c => !c.isArchived),
			itemsTree: state => state.items.itemsTree,
			seedsId: state => state.configs.itemsConfigs.seedsId
		}),
		...mapGetters({
			getAccountById: 'accounts/getAccountById',
			getItemById: 'items/getItemById'
		}),
		
		headers() {
			return [
				{
					text: this.$T('headers.lot'),
					name: 'lot',
					value: 'lot',
					type: 'number-format'
				},
				{
					text: this.$T('headers.date'),
					name: 'date',
					value: 'date',
					type: 'date'
				},
				{
					text: this.$T('headers.weight') + ' (qq)',
					name: 'weight',
					value: 'qqNetWeight',
					type: 'number-format'
				},
				{
					text: this.$T('headers.total-price') + ' (Q)',
					name: 'totalPurchase',
					value: 'totalPurchase',
					type: 'number-format'
				},
				/* {
					text: this.$T('headers.px-cal'),
					name: 'pxCal',
					value: 'pxCal',
					type: 'number-format'
				}, */
				{
					text: this.$T('headers.entry-no'),
					name: 'entryNo',
					value: 'income',
				},
				{
					text: this.$T('headers.owner'),
					name: 'ownerId',
					value: 'ownerId',
				},
				{
					text: this.$T('headers.provider'),
					name: 'providerId',
					value: 'providerId',
					setValueExpr: val => this.providers.find(c => c.id === val)?.name
				},
				{
					text: '',
					name: 'actions',
					value: 'actions',
					width: 140
				}
			]
		},

		dialogHeaders() {
			return [
				{
					text: this.$T('headers.item'),
					name: 'itemName',
					value: 'itemName',
					columnClass: 'px-2',
				},
				{
					text: this.$T('headers.weight'),
					name: 'weight',
					value: 'weight',
					columnClass: 'px-2',
					align: 'center',
					type: 'number-format',
					width: 180,
					printValueExpr: val => this.$options.filters.numberFormat(val || 0),
					noNotAvailableText: true,
				},
				{
					text: '%',
					name: 'percent',
					value: 'percent',
					class: 'text-h6',
					columnClass: 'px-2',
					type: 'number',
					printValueExpr: val => fixNumber(val || 0),
					inputRules: rules.requiredAsPercent,
					/* onWriteChange: (item, activeInput, index) => {
						if (!this.dialogData.entires[index] || !item.parentId) return;
						const parentIndex = this.dialogData.entires.findIndex(c => c.id === item.parentId);
						
						this.dialogData.entires[parentIndex].percent =
							this.dialogData.entires[parentIndex].originalPercent -
							this.dialogData.entires
								.filter(c => c.parentId === item.parentId && c.id !== item.id)
								.map(c => c.percent)
								.reduce((a, b) => a + b, 0) -
							this.dialogData.entires[index].percent

						this.dialogData.entires[index].weight =
							fixNumber((this.dialogData.entires[index].percent / 100) * this.dialogData.weight);
						this.updateItemsWeights();
					}, */
					width: '30%',
				},
				{
					text: '',
					name: 'actions',
					value: 'actions',
					width: 0,
					columnClass: 'px-0',
					noNotAvailableText: true,
					sortable: false,
					noInput: true,
					noPrint: true,
				},
			]
		},

		itemsHeaders() {
			const subTreeItemId = this.cardamomRootId || this.itemsTree[0].id;
			if (!subTreeItemId) return;
			const subTree = this.itemsTree.find(c => c.id === subTreeItemId);

			const headers = [];
			const getCellColor = (priority) => {
				switch (priority) {
				case 1: return 'primary-priority-2';
				case 2: return 'secondary-priority-2';
				}
				if (priority < 10) {
					return `thirdly-priority-${priority - 1}`
				}
				return ''
			}

			subTree.children.forEach((c, i) => {
				headers.push({
					text: c.name,
					name: c.name,
					value: c.id,
					align: 'center',
					width: 65,
					type: 'number-format',
					cellClass: getCellColor(i + 1),
					columnClass: 'px-1',
					itemCategory: c.itemCategory
				});
				if (c.children.length) {
					c.children.forEach(e => {
						headers.push({
							text: e.name,
							name: e.name,
							value: e.id,
							align: 'center',
							width: 65,
							type: 'number-format',
							cellClass: getCellColor(i + 1),
							columnClass: 'px-1',
							itemCategory: c.itemCategory
						})
					});
				}
			})

			return headers;
		},

		reportDialogHeaders() {
			const headers = [
				{ text: 'lot', name: 'lot', value: 'lot' },
				{ text: this.$T('headers.total') + ' (qq)', name: 'total', type: 'number-format', value: 'total' },
				...this.itemsHeaders.filter(c => c.itemCategory === itemCategory.commercial)
			];

			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-1' : 'px-1';
				headers[i].align = 'center';
				headers[i].type = 'number-format';
			}
			return headers;
		},

		tableItems() {
			return this.dialogData.entires.filter(c =>
				(!this.dialogData.isSeed && c.itemCategory === itemCategory.commercial)
					? true
					: (c.id === this.seedsId && c.itemCategory === itemCategory.commercial)
			)
		}
	},

	methods: {
		getTotalChildrenPercent(entries, parentId) {
			return entries.filter(c => c.parentId === parentId).reduce((res, a) => res + +a.percent, 0);
		},

		getTotalParentPercent(entries) {
			return entries.filter(c => c.parentId === null).reduce((res, a) => res + +a.percent, 0)
		},

		getParentWeight(id) {
			const item = this.dialogData.entires.find(c => c.id === id);
			if (item) return item.weight;
		},

		updateItemsWeights(val) {
			if (val) {
				this.dialogData.totalPurchase = this.dialogData.weight * this.dialogData.price;
				for (let i = 0; i < this.dialogData.entires.length; i++) {
					const entry = this.dialogData.entires[i];
					const weight =
						entry.parentId ? this.getParentWeight(entry.parentId) : this.dialogData.weight

					this.dialogData.entires[i].weight =
						fixNumber((this.dialogData.entires[i].percent / 100) * weight);
				}
			}
		},

		updatePurchaseEntires() {
			this.dialogData.entires = [];
			const purchaseSubTreeItemId = this.cardamomRootId || this.itemsTree[0].id;
			if (!purchaseSubTreeItemId) return;
			const subTree = this.itemsTree.find(c => c.id === purchaseSubTreeItemId);

			subTree.children.forEach(c => {
				this.dialogData.entires.push({
					id: c.id,
					itemName: c.name,
					parentName: null,
					parentId: null,
					hasChildren: Boolean(c.children && c.children.length > 0),
					weight: 0,
					percent: 0,
					itemCategory: c.itemCategory
				})
				if (!c.children.length) return;

				c.children.forEach(e => {
					this.dialogData.entires.push({
						id: e.id,
						itemName: e.name,
						parentName: c.name,
						parentId: e.parentId,
						hasChildren: false,
						weight: 0,
						percent: 0,
						itemCategory: c.itemCategory
					})
				})
			})
		},

		// fetching data
		filter(newScrollOptions, isFirstLoad) {
			let page = 1;
			let itemsPerPage = 20;
			if (newScrollOptions) {
				page = newScrollOptions.page;
				itemsPerPage = newScrollOptions.itemsPerPage;
			} else if (this.$refs.table) {
				this.$refs.table.resetShownRows();
			}
			
			this.loading = true;
			return this.$store.dispatch('receives/fetchAll', {
				date: this.filterData.date,
				status: this.filterData.status,
				page: page,
				perPage: itemsPerPage || 20
			}).then(({ data, total }) => {
				this.totalItems = total;
				this.$refs.table.addScrollItems(data);
			}).finally(() => {
				if (!isFirstLoad) this.loading = false;
			})
		},

		fetchReceivesStatics() {
			return this.$store.dispatch('receives/fetchReceivesStatics', {
				date: this.filterData.date,
				status: this.filterData.status
			})
		},

		generateReport(newScrollOptions) {
			let page = 1;
			let itemsPerPage = 20;

			if (newScrollOptions) {
				page = newScrollOptions.page;
				itemsPerPage = newScrollOptions.itemsPerPage;
			} else if (this.$refs.reportTable) {
				this.$refs.reportTable.resetShownRows()
			}
			this.loadingReport = true;
			this.$store.dispatch('receives/fetchReport', {
				// reportTable
				date: this.reportDialogFilters.date,
				lot: this.reportDialogFilters.lot,
				page,
				perPage: itemsPerPage
			}).then(({ data, total }) => {
				const items = []
				this.totalReportItemsLength = total;
				data.forEach((c, i) => {
					items.push({
						id: Date.now() + i,
						lot: c.lot,
						total: c.purchaseItems.reduce((res, a) =>
							res +
							(this.getItemById(a.itemId).parentId === this.cardamomRootId ? a.weight : 0)
						, 0),
						...this.itemsHeaders.reduce((res, header) => {
							res[header.value] =
								c.purchaseItems.find(c => c.itemId === header.value)?.weight ?? null;
							return res;
						}, {})
					})
				});
				this.getStatisticsTotalReport()
				this.$refs.reportTable.addScrollItems(items);
			}).catch(() => {
				this.loadingReport = false;
			})
		},
		getStatisticsTotalReport () {
			return this.$store.dispatch('receives/fetchStatisticsTotal', {
				date: this.reportDialogFilters.date ? this.reportDialogFilters.date : this.filterData.date,
				status: this.filterData.status,
				lot: this.reportDialogFilters.lot
			}).then(data => {
				this.allTotalReports = data
				this.reportDialog = true;
				this.loadingReport = false
			}).catch(() => {
				this.reportDialog = true
				this.loadingReport = false
			})
		},

		// submit
		submit() {
			if (!this.dialogData.isSeed) {
				const mainItemsIds = this.dialogData.entires.filter(c => c.hasChildren).map(c => c.id);
				const subItems = this.dialogData.entires.filter(c => !c.hasChildren);
				for (let i = 0; i < mainItemsIds.length; i++) {
					const id = mainItemsIds[i];
					const totalPercent =
						subItems.filter(c => c.parentId === id).reduce((sum, a) => sum + a.percent, 0);
					if (totalPercent !== 100) return;
				}
			}
			
			if (this.$refs.form.validate() && this.getTotalParentPercent(this.dialogData.entires) === 100) {
				this.submitLoading = true;
				this.$store.dispatch('receives/update', {
					id: this.receiveItem.id,
					purchaseId: this.receiveItem.purchaseId,
					lot: this.dialogData.lot,
					date: this.dialogData.date,
					ownerId: this.dialogData.ownerId,
					income: this.dialogData.income,
					quality: '',
					qqNetWeight: this.dialogData.weight,
					price: this.dialogData.price,
					totalPurchase: this.dialogData.totalPurchase,
					specialInvoice: this.dialogData.specialInvoice,
					creditNote: this.dialogData.creditNote,
					pacoReference: this.dialogData.pacoReference,
					note: this.dialogData.notes,
					items: this.dialogData.entires.map(c => ({
						itemId: c.id,
						weight: c.weight,
						percent: c.percent
					})),
					humidity: this.dialogData.humidity || null,
					excludeFromStatistics: this.dialogData.excludeFromStatistics
				}).then(() => {
					this.$eventBus.$emit('show-snackbar', this.$T('messages.purchase-added'), 'info');
					this.receiveDialog = false;
					
					this.loading = true;
					Promise.all([
						this.filter(null, true),
						this.fetchReceivesStatics()
					]).finally(() => { this.loading = false; });
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},
		fixNumber,

		generatePrint() {
			const headers = this.dialogHeaders.filter(c => !c.noPrint);
			const customTitleHtml = [
				{
					text: this.$T('inputs.lot'),
					value: this.dialogData.lot,
					width: '10%',
				},
				{
					text: this.$T('inputs.entry-no'),
					value: this.dialogData.income,
					width: '10%',
				},
				{
					text: this.$T('inputs.provider'),
					value: this.getAccountById(this.dialogData.providerId)?.name || '',
					width: '20%',
				},
				{
					text: this.$T('inputs.weight'),
					value: this.$options.filters.numberFormat(this.dialogData.weight || 0) + ' qq',
					width: '12%',
				},
				{
					text: this.$T('inputs.price'),
					value: this.$options.filters.numberFormat(this.dialogData.price || 0) + ' Q',
					width: '20%',
				},
				{
					text: this.$T('inputs.humidity') + ' (%)',
					value: this.dialogData.humidity || '',
					width: '10%',
				},
				{
					text: this.$T('inputs.total-price'),
					value: this.$options.filters.numberFormat(this.dialogData.totalPurchase || 0) + ' Q',
					width: '20%',
				},
				{
					text: this.$T('inputs.special-invoice'),
					value: this.dialogData.specialInvoice || '',
					width: '10%',
				},
				{
					text: this.$T('inputs.credit-note'),
					value: this.dialogData.creditNote || '',
					width: '10%',
				},
				{
					text: this.$T('inputs.paco-reference'),
					value: this.dialogData.pacoReference || '',
					width: '10%',
				},
				{
					text: this.$T('inputs.notes'),
					value: this.dialogData.notes || '',
					width: '100%',
				},
			].filter(c => c.value)
				.map(({ text, value, width }) => `
					<div style="max-width: ${width || '15%'}; margin-right: 2%; white-space: nowrap;">
						${text}: <b>${value}</b>
					</div>
				`)
				.join('');
			
			const html = generateHtmlPdf({
				titleData: {
					Type: 'Purchase Report',
					Owner: this.getAccountById(this.receiveItem.ownerId)?.name || 'Ugarit',
					Date: this.dialogData.date
				},
				customTitleHtml: `<div style="display: flex; flex-wrap: wrap; margin-top: 10px;">${customTitleHtml}</div>`,
				headers: headers.map(c => c.text),
				data: this.tableItems.map(item =>
					headers.map(header => 
						header.printValueExpr
							? header.printValueExpr(item[header.value])
							: header.setValueExpr
								? header.setValueExpr(item[header.value])
								: item[header.value]
					)
				)
			});
			
			printJS({
				printable: html,
				type: 'raw-html',
			})
		},
	},

	created() {
		this.loading = true;
		Promise.all([
			this.filter(null, true),
			this.fetchReceivesStatics()
		]).finally(() => {
			this.loading = false;
		})
		
		if (this.$route.query.receivingId) {
			this.isPreview = true;
			this.receiveDialog = true;
		}
	},

	/* beforeRouteEnter(to, from, next) {
		next(vm => {
			if (vm.permissions.receives.isNone) {
				next({ name: 'home', replace: true });
			}
			return true;
		});
	} */
}
</script>

<style>
	#dialogTable td {
		height: 20px;
	};
	#dialogTable tr {
		height: 20px;
	}
	#dialogTable thead.v-data-table-header {
		height: 20px;
	}
</style>